import { DeploySet, DeployState, SelectedSystem } from '@aero-platform/shared'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useState } from 'react'
import { Control, Controller, FieldError, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { SelectOption } from '../types/types'

type MethodSetSectionProps = {
  sectionKey: string
  sectionIndex: number
  sectionName: string
  deploySets: DeploySet[]
  selectedSystem: SelectedSystem
  control: Control<any>
  setValue: UseFormSetValue<any>
  disabled?: boolean
  mode: string
  formErrors?: FieldErrors
}
const MethodSetSection: FC<MethodSetSectionProps> = ({
  sectionIndex,
  deploySets,
  sectionName,
  selectedSystem,
  control,
  setValue,
  disabled = false,
  mode,
  formErrors
}) => {
  if (sectionIndex === undefined || sectionIndex === -1) {
    // return null
  }
  const [vendorVersions, setVendorVersions] = useState<SelectOption[]>([])
  const [branchNames, setBranchNames] = useState<SelectOption[]>([])
  const [commits, setCommits] = useState<DeploySet[]>([])
  const [scripts, setScripts] = useState<DeployState[]>([])
  const [selectedVersion, setSelectedVersion] = useState<string>(selectedSystem?.vendorVersion)

  const onLazyLoadEntryScript = () => {}

  const loadingEntryScript = false

  const getUniqueBranches = (vendorVersion: string) => {
    if (!deploySets) return []
    const versions = new Set(
      deploySets
        .filter(item => item.vendorCompatibilityVersion === vendorVersion)
        .map(item => {
          return item.version
        })
    )

    return Array.from(versions)
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }))
      .map(item => ({ label: item, value: item }))
  }

  const getCommits = (vendorVersion: string, version: string) => {
    if (!deploySets) return []
    const commits = deploySets
      .filter(item => {
        return item.vendorCompatibilityVersion === vendorVersion && item.version === version
      })
      .map(item => {
        return {
          _id: item._id,
          commitId: item.commitId,
          deployStates: item.deployStates
        } as DeploySet
      })

    return commits
  }

  useEffect(() => {
    if (deploySets && deploySets.length > 0) {
      const uniqueVersions = []
      deploySets.forEach(item => {
        if (uniqueVersions.includes(item.vendorCompatibilityVersion)) {
          return
        }
        uniqueVersions.push(item.vendorCompatibilityVersion)
      })
      setVendorVersions(uniqueVersions.map(item => ({ label: item, value: item })))
    }
  }, [])

  useEffect(() => {
    if (mode === 'add') {
      return
    }

    // for edit mode
    if (selectedSystem?.vendorVersion) {
      // filter branches based on selected vendor version
      setBranchNames(getUniqueBranches(selectedSystem.vendorVersion))

      if (selectedSystem?.branchName) {
        const commits = getCommits(selectedSystem.vendorVersion, selectedSystem.branchName)
        setCommits(commits || [])

        if (selectedSystem?.commitId) {
          const deploySet = commits.find(item => item.commitId === selectedSystem.commitId)
          deploySet && setScripts(deploySet.deployStates)
        } else {
          setScripts([])
        }
      } else {
        setCommits([])
        setScripts([])
      }
    } else {
      setBranchNames([])
      setCommits([])
      setScripts([])
    }
  }, [selectedSystem, sectionIndex, mode])

  const getFormErrorMessage = name => {
    if (!formErrors) {
      return <small className="error">&nbsp;</small>
    }
    const parts = name.split('.')
    let errors: FieldError | FieldErrors | null = formErrors
    for (let i = 0; i < parts.length; i++) {
      if (errors[parts[i]]) {
        errors = errors[parts[i]]
      } else {
        errors = null
        break
      }
    }
    return errors ? (
      <small className="error">{errors.message as string}</small>
    ) : (
      <small className="error">&nbsp;</small>
    )
  }

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-4">
      <div className="field-box">
        <Controller
          name={`selectedSystems.${sectionIndex}.vendorVersion`}
          control={control}
          rules={{ required: 'Version is required.' }}
          render={({ field, fieldState }) => (
            <>
              <label htmlFor={field.name} className="mb-2">
                {`${sectionName} Version`} <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={field.value}
                disabled={disabled}
                filter
                resetFilterOnHide={false}
                emptyFilterMessage="No matching Version found"
                pt={{
                  root: {
                    className:
                      'min-w-full bg-transparent dark:border-neutral-70 dark:bg-transparent'
                  },
                  input: {
                    className: 'py-2'
                  }
                }}
                onChange={e => {
                  field.onChange(e.value)
                  setSelectedVersion(e.value)
                  setBranchNames(getUniqueBranches(e.value))
                  setValue(`selectedSystems.${sectionIndex}.branchName`, null)
                  setValue(`selectedSystems.${sectionIndex}.commitId`, null)
                  setValue(`selectedSystems.${sectionIndex}.entryScriptId`, null)
                }}
                className={classNames({ error: fieldState.error })}
                options={vendorVersions}
                placeholder="Select Version"
                optionLabel="label"
                optionValue="value"
                emptyMessage="&nbsp;&nbsp;No Version Data"
              />
              {getFormErrorMessage(field.name)}
            </>
          )}
        />
      </div>
      <div className="field-box">
        <Controller
          name={`selectedSystems.${sectionIndex}.branchName`}
          control={control}
          rules={{ required: 'Pipeline release version is required.' }}
          render={({ field, fieldState }) => (
            <>
              <label htmlFor={field.name} className="mb-2">
                Pipeline Release Version <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={field.value}
                disabled={disabled}
                filter
                resetFilterOnHide={false}
                emptyFilterMessage="No matching Pipeline Release Version found"
                pt={{
                  root: {
                    className:
                      'min-w-full bg-transparent dark:border-neutral-70 dark:bg-transparent'
                  },
                  input: {
                    className: 'py-2'
                  }
                }}
                onChange={e => {
                  field.onChange(e.value)
                  setCommits(getCommits(selectedVersion, e.value))
                  setValue(`selectedSystems.${sectionIndex}.commitId`, null)
                  setValue(`selectedSystems.${sectionIndex}.entryScriptId`, null)
                }}
                className={classNames({ error: fieldState.error })}
                options={branchNames}
                placeholder="Select Version"
                optionLabel="label"
                optionValue="value"
                emptyMessage="&nbsp;&nbsp;No Pipeline Release Version Data"
              />
              {getFormErrorMessage(field.name)}
            </>
          )}
        />
      </div>
      <div className="field-box">
        <Controller
          name={`selectedSystems.${sectionIndex}.commitId`}
          control={control}
          rules={{ required: 'Pipeline release commit is required.' }}
          render={({ field, fieldState }) => (
            <>
              <label htmlFor={field.name} className="mb-2">
                Pipeline Release Commit <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={field.value}
                disabled={disabled}
                filter
                resetFilterOnHide={false}
                emptyFilterMessage="No matching Pipeline Release Commit found"
                pt={{
                  root: {
                    className:
                      'min-w-full bg-transparent dark:border-neutral-70 dark:bg-transparent'
                  },
                  input: {
                    className: 'py-2'
                  }
                }}
                onChange={e => {
                  field.onChange(e.value)
                  if (e.value) {
                    setScripts(commits.find(item => item.commitId === e.value).deployStates)
                  }
                  setValue(`selectedSystems.${sectionIndex}.entryScriptId`, null)
                }}
                className={classNames({ error: fieldState.error })}
                options={commits}
                placeholder="Select Pipeline Release"
                optionLabel="commitId"
                optionValue="commitId"
                emptyMessage="&nbsp;&nbsp;No Pipeline Release Commit Data"
              />
              {getFormErrorMessage(field.name)}
            </>
          )}
        />
      </div>
      <div className="field-box">
        <Controller
          name={`selectedSystems.${sectionIndex}.entryScriptId`}
          control={control}
          rules={{ required: 'Entry script is required.' }}
          render={({ field, fieldState }) => (
            <>
              <label htmlFor={field.name} className="mb-2">
                Entry Script <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={field.value}
                disabled={disabled}
                filter
                resetFilterOnHide={false}
                emptyFilterMessage="No matching scripts found"
                pt={{
                  root: {
                    className:
                      'min-w-full bg-transparent dark:border-neutral-70 dark:bg-transparent'
                  },
                  input: {
                    className: 'py-2'
                  }
                }}
                onChange={e => {
                  field.onChange(e.value)
                }}
                className={classNames({ error: fieldState.error })}
                options={scripts}
                placeholder="Select Script"
                optionLabel="name"
                optionValue="name"
                emptyMessage="&nbsp;&nbsp;No Entry Script Data"
              />
              {getFormErrorMessage(field.name)}
            </>
          )}
        />
      </div>
      <div className="field-box col-span-4">
        <Controller
          name={`selectedSystems.${sectionIndex}.notes`}
          control={control}
          disabled={disabled}
          render={({ field, fieldState }) => (
            <>
              <label htmlFor={field.name} className="mb-2">
                Notes
              </label>
              <InputText
                id={field.name}
                {...field}
                className={classNames({ error: fieldState.error })}
              />
            </>
          )}
        />
      </div>
    </div>
  )
}

export default MethodSetSection
